<template>
  <div class="gallery app-page-wrapper absolute" data-cy="gallery">
    <div class="w-full h-full relative flex flex-col items-center">
      <menu-component menu-type="collection" :title="$t('gallery.gallery')" />

      <section
        v-if="pictureId && picture"
        class="w-full h-full overflow-hidden safe-area bg-cover flex"
        :style="{
          backgroundImage: 'url(' + pathImages + 'gallery/BG_FULLSCREEN.avif)',
        }"
      >
        <div
          class="main-wrapper h-full flex justify-center items-start relative main-gallery-content-wrapper"
        >
          <gallery-sub-header
            :collection-name="collectionName"
            :show-collection-name="!isRepeatable && !isSpecial"
            :is-repeatable="isRepeatable"
            :is-special="isSpecial"
            :is-premium="isPremium"
          >
            <span class="uppercase">{{ $t(`gallery.${pictureParams?.name}`) }}</span>
            <progress-status v-if="isInProgress" :percent="picture.percentage_completed || 0" class="progress-status-position" />
          </gallery-sub-header>
          
          <div class="w-full h-full justify-between flex flex-wrap ml-14 mr-8 pt-5">
            <gallery-picture-detail />
          </div>
        </div>

        <div class="flex items-center flex-col">
          <gallery-item-complete
            :is-completed="isCompleted"
            :is-claimable="canClaim"
            :rewards="picture.rewards"
            :img-id="pictureParams?.id"
            lower-version
            is-picture
            @claim-reward="claimReward(picture.id)"
          />

          <gallery-puzzle-needed-counter />
        </div>
      </section>

      <component-loading :is-loading="!picture" />
    </div>
  </div>
</template>

<script lang="ts">
import { pathImages } from '@/globalVariables'
import { defineComponent } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useGalleryPictureStore } from '@/store/pinia/gallery/pictureStore'
import GalleryItemComplete from '@/components/Gallery/GalleryItemComplete.vue'
import GallerySubHeader from '@/components/Gallery/GallerySubHeader.vue'
import MenuComponent from '@/components/GlobalComponents/MenuComponent.vue'
import GalleryPictureDetail from '@/components/Gallery/GalleryPictureDetail.vue'
import GalleryPuzzleNeededCounter from '@/components/Gallery/GalleryPuzzleNeededCounter.vue'
import ProgressStatus from '@/components/Gallery/ProgressStatus.vue'
import { GalleryPictureCategory, GalleryPictureState } from '@/interfaces/gallery/enums'
import type { GalleryPicture } from '@/interfaces/gallery/Pictures'

interface ComponentData {
  pathImages: typeof pathImages
  pictureId: string
}

export default defineComponent({
  name: 'GalleryPicture',

  components: {
    GalleryItemComplete,
    GallerySubHeader,
    MenuComponent,
    GalleryPictureDetail,
    GalleryPuzzleNeededCounter,
    ProgressStatus,
  },

  data(): ComponentData {
    return {
      pathImages,
      pictureId: ''
    }
  },

  computed: {
    ...mapState(useGalleryPictureStore, ['picture']),

    pictureParams(): GalleryPicture['parameters'] | null {
      if (!this.picture || !this.picture.parameters) return null
      return this.picture.parameters
    },

    isRepeatable(): boolean {
      return this.pictureParams.category === GalleryPictureCategory.Repeatable
    },

    isSpecial(): boolean {
      return this.pictureParams.category === GalleryPictureCategory.Special
    },

    collectionName(): string {
      if (this.isRepeatable || this.isSpecial || !this.picture.collection) return ''

      const name = this.$t(`gallery.${this.picture.collection}`)
      if (this.picture.collection === 'collection100Meters') {
        return this.$replacePlaceholder(name, '%s', '100')
      }
      return name
    },

    isPremium(): boolean {
      return !!this.pictureParams && !this.pictureParams.is_free
    },

    isCompleted(): boolean {
      return this.picture?.state === GalleryPictureState.CompletedClaimed
    },

    isInProgress(): boolean {
      return this.picture?.state === GalleryPictureState.InProgress
    },

    canClaim(): boolean {
      return this.picture?.state === GalleryPictureState.CompletedUnclaimed
    }
  },


  created(): void {
    this.pictureId = this.$route.params.id.toString() || ''

    if (!this.pictureId) {
      this.$router.push({ name: this.$getWebView('GalleryView') })
      return
    }

    this.loadPicture(this.pictureId)
  },

  methods: {
    ...mapActions(useGalleryPictureStore, ['loadPicture', 'claimReward'])
  }
})
</script>

<style lang="scss" scoped>
@import '@/assets/styles/views/gallery/gallery.scss';

.main-gallery-content-wrapper {
  padding-top: 5.5rem;
  padding-bottom: 1.375rem;
  width: calc(100% - 2.5rem - 29.375rem);
}
</style>
